import { SEO_QUERY, IMAGE_QUERY_FIELDS } from './shared';
import {
  SECTION_TEXT_AND_SUBTITLE_QUERY,
  SECTION_TITLE_AND_TEXT_QUERY,
  SECTION_CTA_QUERY,
  SECTION_DOUBLE_CTA_QUERY,
  SECTION_KLAVIYO,
  SECTION_COMPONENT_CONTAINER_QUERY,
} from './sections';

export const FAQ_HUB_SLUG = 'faq';

export const FAQ_SIDEBAR_CATEGORY_LIMIT = 50;
export const FAQ_QUESTION_PATHS_LIMIT = 500;
export const FAQ_SECTIONS_LIMIT = 25;
export const FAQ_ACCORDION_QUESTIONS_LIMIT = 50;

// Base sidebar fields for a single FAQ category. Used both at the top level and
// for a category referenced via `childCategory` (one level of nesting — the
// content team can drop a sub-category under a parent in the sidebar).
//
// Menu order is set by the position of each entry in the hub's `categories`
// array (`pageFaqHub.categoriesCollection`), so the category's `order` field
// is no longer read.
const FAQ_CATEGORY_SIDEBAR_BASE_FIELDS = `
  sys { id }
  name
  slug
  title
  shortLabel
`;

export const FAQ_CATEGORY_SIDEBAR_FIELDS = `
  ${FAQ_CATEGORY_SIDEBAR_BASE_FIELDS}
  childCategory {
    ... on PageFaqCategory {
      ${FAQ_CATEGORY_SIDEBAR_BASE_FIELDS}
    }
  }
`;

// The hub's `categories` collection plus the hub's own title/urlSlug. Drives
// the FAQ sidebar AND the breadcrumb's "hub" link (which needs the localized
// hub title and the URL prefix).
export const FAQ_HUB_SIDEBAR_CATEGORIES_QUERY = `
  title
  urlSlug
  categoriesCollection(limit: ${FAQ_SIDEBAR_CATEGORY_LIMIT}) {
    items {
      ... on PageFaqCategory {
        ${FAQ_CATEGORY_SIDEBAR_FIELDS}
      }
    }
  }
`;

// Section type contributed by the FAQ feature. Lists pageFaqQuestion entries
// as expandable items: opening one shows a short preview derived from the
// question's answer rich text plus a "read more" link to its dedicated
// page. Only `answer { json }` is fetched here — the preview is extracted and
// truncated at parse time (lib/utils/faqAnswerPreview) so page props stay
// small even with ${FAQ_ACCORDION_QUESTIONS_LIMIT} questions.
export const SECTION_FAQ_ACCORDION_QUERY = `
  ... on SectionFaqAccordion {
    anchorId
    __typename
    sys { id }
    name
    title
    questionsCollection(limit: ${FAQ_ACCORDION_QUESTIONS_LIMIT}) {
      items {
        ... on PageFaqQuestion {
          sys { id }
          slug
          question
          shortLabel
          answer { json }
          category {
            ... on PageFaqCategory {
              slug
            }
          }
        }
      }
    }
  }
`;

// Section types allowed inside pageFaqCategory.sections and pageFaqQuestion.sections.
// Mirrors the Contentful content type validation. Targeted subset for now;
// expand as content team needs more.
//
// NOTE: sectionContactForm is intentionally excluded — it declares `button` as
// a Symbol while sectionCta/sectionTitleAndText declare `button` as a Link, so
// putting them in the same union triggers a GraphQL field-conflict error.
// Klaviyo covers most form needs; if a true contact form is required, the
// SECTION_CONTACT_FORM fragment needs a `buttonLabel` alias before it can join
// this bundle.
export const FAQ_PAGE_SECTIONS_FRAGMENT = `
  ${SECTION_TEXT_AND_SUBTITLE_QUERY}
  ${SECTION_TITLE_AND_TEXT_QUERY}
  ${SECTION_CTA_QUERY}
  ${SECTION_DOUBLE_CTA_QUERY}
  ${SECTION_KLAVIYO}
  ${SECTION_COMPONENT_CONTAINER_QUERY}
  ${SECTION_FAQ_ACCORDION_QUERY}
`;

export const FAQ_CATEGORY_DETAIL_FIELDS = `
  ${FAQ_CATEGORY_SIDEBAR_FIELDS}
  sectionsCollection(limit: ${FAQ_SECTIONS_LIMIT}) {
    items {
      ${FAQ_PAGE_SECTIONS_FRAGMENT}
    }
  }
  seo {
    ${SEO_QUERY}
  }
`;

// Rich text fields for pageFaqQuestion.answer. The field's validations only
// allow embedded ASSETS (images) — no embedded entries — so this is a slimmer
// links fragment than the shared RICH_TEXT_WITH_ASSETS_FIELDS, whose
// ComponentMedia entries bundle pushed the question detail query over
// Contentful's 8192-byte query size limit.
const FAQ_ANSWER_RICH_TEXT_FIELDS = `
  json
  links {
    assets {
      block {
        __typename
        ${IMAGE_QUERY_FIELDS}
      }
    }
  }
`;

export const FAQ_QUESTION_DETAIL_FIELDS = `
  sys { id }
  name
  slug
  question
  shortLabel
  answer {
    ${FAQ_ANSWER_RICH_TEXT_FIELDS}
  }
  category {
    ... on PageFaqCategory {
      ${FAQ_CATEGORY_SIDEBAR_FIELDS}
    }
  }
  sectionsCollection(limit: ${FAQ_SECTIONS_LIMIT}) {
    items {
      ${FAQ_PAGE_SECTIONS_FRAGMENT}
    }
  }
  seo {
    ${SEO_QUERY}
  }
`;

// Body fields shared by both FAQ hub content types — the product hub
// (`pageFaqHub`) and the customer-service hub (`pageCustomerService`). Both
// expose the same intro + categories + SEO shape; only the GraphQL collection
// name differs at the query layer.
export const FAQ_HUB_FIELDS = `
  sys { id }
  name
  title
  intro {
    json
  }
  ${FAQ_HUB_SIDEBAR_CATEGORIES_QUERY}
  seo {
    ${SEO_QUERY}
  }
`;
